import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Dialog } from '@headlessui/react';
import { Button, Icon, Modal } from '../../../../shared/ui';
import { IconMap } from '../../../../shared/sprite';

interface ImageCropperProps {
  src: string;
  onCropComplete: (croppedImage: File) => void;
  onCancel: () => void;
}

// Fixed aspect ratio of 1024:250 (page cover size)
const ASPECT_RATIO = 1024 / 250;
const TARGET_WIDTH = 1024;
const TARGET_HEIGHT = 250;

export const ImageCropper: React.FC<ImageCropperProps> = ({
  src,
  onCropComplete,
  onCancel,
}) => {
  const [crop, setCrop] = useState<Crop | null>(null);
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const makeProperCrop = (imageWidth: number, imageHeight: number): Crop => {
    const cropHeight = imageWidth / ASPECT_RATIO;

    if (cropHeight > imageHeight) {
      // Take full height and calculate width
      const cropWidth = imageHeight * ASPECT_RATIO;
      // Center horizontally
      const x = (imageWidth - cropWidth) / 2;

      return {
        unit: 'px',
        width: cropWidth,
        height: imageHeight,
        x: x,
        y: 0,
      };
    } else {
      // Take full width and position crop vertically
      // Center the crop vertically
      const y = (imageHeight - cropHeight) / 2;

      return {
        unit: 'px',
        width: imageWidth,
        height: cropHeight,
        x: 0,
        y: y,
      };
    }
  };

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const image = e.currentTarget;
    imgRef.current = image;

    // Set initial crop that takes full width
    const initialCrop = makeProperCrop(image.width, image.height);
    setCrop(initialCrop);
    setCompletedCrop(initialCrop);
  }

  const ensureValidCrop = (newCrop: Crop): Crop => {
    if (!imgRef.current) return newCrop;

    const image = imgRef.current;

    const validCrop: Crop = { ...newCrop };

    if (validCrop.x < 0) validCrop.x = 0;
    if (validCrop.y < 0) validCrop.y = 0;

    const rightBoundary = Math.max(0, image.width - validCrop.width);
    const bottomBoundary = Math.max(0, image.height - validCrop.height);

    if (validCrop.x > rightBoundary) validCrop.x = rightBoundary;
    if (validCrop.y > bottomBoundary) validCrop.y = bottomBoundary;

    return validCrop;
  };

  const handleCropChange = useCallback((newCrop: Crop) => {
    const validCrop = ensureValidCrop(newCrop);

    setCrop(validCrop);
    setCompletedCrop(validCrop);
  }, []);

  const handleCropComplete = useCallback((c: Crop) => {
    const validCrop = ensureValidCrop(c);
    setCompletedCrop(validCrop);
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!crop || !imgRef.current) return;

      const imgRect = imgRef.current.getBoundingClientRect();
      const cropLeft = imgRect.left + crop.x;
      const cropRight = cropLeft + crop.width;
      const cropTop = imgRect.top + crop.y;
      const cropBottom = cropTop + crop.height;

      if (
        e.clientX < cropLeft ||
        e.clientX > cropRight ||
        e.clientY < cropTop ||
        e.clientY > cropBottom
      ) {
        e.preventDefault();
      }
    },
    [crop]
  );

  const getCroppedImg = async () => {
    if (!imgRef.current || !completedCrop) return;

    try {
      const image = imgRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = TARGET_WIDTH;
      canvas.height = TARGET_HEIGHT;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      let sourceX, sourceY, sourceWidth, sourceHeight;

      if (completedCrop.unit === 'px') {
        sourceX = completedCrop.x * scaleX;
        sourceY = completedCrop.y * scaleY;
        sourceWidth = completedCrop.width * scaleX;
        sourceHeight = completedCrop.height * scaleY;
      } else {
        sourceX = ((completedCrop.x * image.width) / 100) * scaleX;
        sourceY = ((completedCrop.y * image.height) / 100) * scaleY;
        sourceWidth = ((completedCrop.width * image.width) / 100) * scaleX;
        sourceHeight = ((completedCrop.height * image.height) / 100) * scaleY;
      }

      ctx.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        TARGET_WIDTH,
        TARGET_HEIGHT
      );

      return new Promise<void>((resolve) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) return;

            const croppedFile = new File([blob], 'cropped-image.jpg', {
              type: 'image/jpeg',
              lastModified: new Date().getTime(),
            });

            onCropComplete(croppedFile);
            resolve();
          },
          'image/jpeg',
          0.95
        );
      });
    } catch (error) {
      console.error('Error in getCroppedImg:', error);
    }
  };

  return (
    <Modal
      isShowCancelButton={false}
      clearPadding={true}
      className="!p-0 w-auto min-w-100"
      isOpen={Boolean(src)}
      onClose={onCancel}
    >
      <Dialog.Title className="flex items-center justify-between flex-shrink-0 w-full px-6 py-4 font-medium text-gray-900 truncate text-display-xs font-display">
        <span className="truncate max-w-[calc(100%-40px)]">Edit cover</span>
        <Icon
          glyph={IconMap.XClose}
          className="text-gray-400 cursor-pointer shrink-0"
          width={24}
          onClick={onCancel}
        />
      </Dialog.Title>

      <div className="flex flex-col items-center justify-center">
        {crop ? (
          <div onMouseDown={handleMouseDown}>
            <ReactCrop
              crop={crop}
              onChange={handleCropChange}
              onComplete={handleCropComplete}
              aspect={ASPECT_RATIO}
              className="max-w-full"
              keepSelection={true}
              ruleOfThirds={true}
            >
              <img
                ref={imgRef}
                src={src}
                onLoad={onImageLoad}
                alt="Crop cover"
                style={{
                  maxWidth: '100%',
                  maxHeight: 'calc(80vh - 130px)',
                  objectFit: 'contain',
                }}
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
              />
            </ReactCrop>
          </div>
        ) : (
          /* This hidden image pre-loads and initializes the crop */
          <div className="flex flex-col items-center">
            <p className="text-gray-500 mb-4">Loading image...</p>
            <img
              src={src}
              alt="Loading"
              onLoad={onImageLoad}
              className="hidden"
            />
          </div>
        )}
      </div>

      <div className="flex justify-between px-6 py-4 mt-auto">
        <Button
          color="secondary"
          variant="text"
          type="button"
          onClick={onCancel}
          className="w-24"
        >
          Cancel
        </Button>
        <Button
          className="w-24"
          color="primary"
          variant="text"
          onClick={getCroppedImg}
          disabled={!crop}
        >
          Apply
        </Button>
      </div>
    </Modal>
  );
};
